// ---------------------------------------------------------------------------
// MISSED JOBS
//
// A job is "missed" when its scheduled day(s) are fully in the past and it was
// never completed, invoiced, skipped or sent back to Needs Scheduling.
//
//   - One-time job: its END date is before today. A multi-day job that started
//     last week and runs through Friday is still in progress, not missed.
//   - Recurring job: one row per series - its most recent missed visit. Any
//     earlier missed visits of the same series ride along as olderMissedKeys
//     so they can be cleared in the same step instead of popping up one after
//     another every day.
//   - Jobs a tech already marked done (pending in techCompletionRequests) are
//     NOT missed - they are waiting in Ready to Invoice.
//
// Every page that shows a missed count uses buildMissedJobRows(), so Home,
// the popup and the Needs Scheduling "Missed" tab always agree.
//
// Writes (all additive, nothing is deleted):
//   - startDate / endDate          one-time reschedule or back to Needs Scheduling
//   - occurrenceOverrides[key]     recurring reschedule (same as Calendar's
//                                  "move this occurrence only")
//   - excludedDates                recurring "skip this visit" (same as
//                                  Close Job -> "this date only")
//   - techCompletionRequests       "It was done" -> Ready to Invoice queue
//   - missedLog[]                  NEW: what happened to each missed visit, why,
//                                  who decided and when
// ---------------------------------------------------------------------------

import { useEffect, useState } from "react";
import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import moment from "moment";
import { db } from "../firebaseConfig";
import {
  applyOccurrenceOverrideToRenderedJob,
  getExcludedDateSet,
  getOccurrenceKeySet,
  getRecurringOccurrenceKey,
  isRecurringJobRecord,
  saveRecurringJobEdit,
} from "./recurringEditUtils";
import {
  TECH_COMPLETION_COLLECTION,
  getTechCompletionRequestId,
  submitTechCompletion,
} from "./techCompletion";

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

export const MISSED_REASONS = [
  "Customer not home / no access",
  "Weather",
  "No tech available",
  "Waiting on materials",
  "Customer cancelled / postponed",
  "Ran out of time",
  "Was done - never checked off",
  "Other",
];

// Same list Home hides from its job list.
const HIDDEN_SERVICE_TYPES = ["Approved Proposal", "Denied Proposal"];

// One-time jobs in any of these states are closed - never "missed".
const CLOSED_STATUSES = [
  "completed",
  "invoice sent",
  "invoiced",
  "closed",
  "cancelled",
  "canceled",
  "paid",
  "paid in full",
];

/* ------------------------------------------------------------------ */
/* Date helpers                                                        */
/* ------------------------------------------------------------------ */

export const toKey = (v) => {
  if (!v) return "";
  if (typeof v === "object" && v?.seconds) {
    return moment(v.seconds * 1000).format("YYYY-MM-DD");
  }
  if (typeof v === "string") {
    const s = v.slice(0, 10);
    return moment(s, "YYYY-MM-DD", true).isValid() ? s : "";
  }
  const m = moment(v);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

export const todayKey = () => moment().format("YYYY-MM-DD");

const addDaysKey = (key, days) =>
  moment(key, "YYYY-MM-DD").add(days, "days").format("YYYY-MM-DD");

const daysBetween = (fromKey, toKeyValue) =>
  moment(toKeyValue, "YYYY-MM-DD").diff(moment(fromKey, "YYYY-MM-DD"), "days");

/* ------------------------------------------------------------------ */
/* Recurrence (mirrors Home.js getHomeOccurrencesForJob so the counts  */
/* match what Home renders - weekday names, job.daysOfWeek fallback,   */
/* customDates and recurrence.endDate are all honoured).               */
/* ------------------------------------------------------------------ */

const getRecurrenceType = (job) => {
  let type =
    typeof job?.recurrence === "object"
      ? job?.recurrence?.type
      : job?.recurrence ?? job?.reccurance ?? job?.recurring ?? job?.repeat ?? "none";

  type = String(type || "none").toLowerCase().trim();
  if (!type || type === "one-time" || type === "onetime") return "none";
  if (type === "bi-weekly" || type === "bi weekly") return "biweekly";
  if (type === "bi-monthly" || type === "bi monthly") return "bimonthly";
  if (type === "bi-yearly" || type === "bi yearly") return "biyearly";
  if (type === "customdates" || type === "custom dates") return "customDates";
  return type;
};

const WEEKDAY_NAMES = {
  sunday: 0, sun: 0,
  monday: 1, mon: 1,
  tuesday: 2, tue: 2, tues: 2,
  wednesday: 3, wed: 3,
  thursday: 4, thu: 4, thur: 4, thurs: 4,
  friday: 5, fri: 5,
  saturday: 6, sat: 6,
};

const normalizeWeekday = (value) => {
  if (typeof value === "number" && value >= 0 && value <= 6) return value;
  const raw = String(value ?? "").trim().toLowerCase();
  if (/^[0-6]$/.test(raw)) return Number(raw);
  return Object.prototype.hasOwnProperty.call(WEEKDAY_NAMES, raw) ? WEEKDAY_NAMES[raw] : null;
};

const getDaysOfWeek = (job, seriesStart) => {
  const recurrence = typeof job?.recurrence === "object" ? job.recurrence : {};
  const rawDays =
    recurrence.daysOfWeek || recurrence.weekDays || recurrence.weekdays ||
    job.daysOfWeek || job.weekDays || job.weekdays || [];
  const normalized = (Array.isArray(rawDays) ? rawDays : [rawDays])
    .map(normalizeWeekday)
    .filter((d) => d !== null);
  return normalized.length ? Array.from(new Set(normalized)) : [seriesStart.day()];
};

// All occurrence keys of a recurring job between startKey and endKey (inclusive).
export const getRecurringOccurrenceKeys = (job, startKey, endKey) => {
  const type = getRecurrenceType(job);
  const recurrence = typeof job?.recurrence === "object" ? job.recurrence : {};
  const seriesStart = moment(toKey(job.startDate), "YYYY-MM-DD", true).startOf("day");
  if (!seriesStart.isValid() || !startKey || !endKey) return [];

  const windowStart = moment(startKey, "YYYY-MM-DD").startOf("day");
  const requestedEnd = moment(endKey, "YYYY-MM-DD").startOf("day");
  const recurrenceEnd = toKey(recurrence.endsOn || recurrence.endDate || job.recurrenceEndDate);
  const hardEnd = recurrenceEnd
    ? moment.min(requestedEnd, moment(recurrenceEnd, "YYYY-MM-DD").startOf("day"))
    : requestedEnd;

  if (hardEnd.isBefore(seriesStart, "day")) return [];

  const dates = new Set();
  const add = (date) => {
    if (!date.isValid()) return;
    if (date.isBefore(seriesStart, "day")) return;
    if (date.isBefore(windowStart, "day")) return;
    if (date.isAfter(hardEnd, "day")) return;
    dates.add(date.format("YYYY-MM-DD"));
  };

  if (type === "customDates") {
    const custom = recurrence.customDates || job.customDates || [];
    (Array.isArray(custom) ? custom : [custom]).forEach((v) => {
      const k = toKey(v);
      if (k) add(moment(k, "YYYY-MM-DD", true));
    });
    return Array.from(dates).sort();
  }

  if (type === "daily") {
    const cursor = moment.max(seriesStart, windowStart).clone();
    while (cursor.isSameOrBefore(hardEnd, "day")) {
      add(cursor);
      cursor.add(1, "day");
    }
    return Array.from(dates).sort();
  }

  if (type === "weekly" || type === "biweekly") {
    const days = getDaysOfWeek(job, seriesStart);
    const interval = type === "biweekly" ? 2 : 1;
    const cursor = moment.max(seriesStart, windowStart).clone();
    while (cursor.isSameOrBefore(hardEnd, "day")) {
      const weeks = cursor.clone().startOf("week").diff(seriesStart.clone().startOf("week"), "weeks");
      if (weeks >= 0 && weeks % interval === 0 && days.includes(cursor.day())) add(cursor);
      cursor.add(1, "day");
    }
    return Array.from(dates).sort();
  }

  const months = { monthly: 1, bimonthly: 2, quarterly: 3, biyearly: 6, yearly: 12 };
  if (months[type]) {
    const cursor = seriesStart.clone();
    while (cursor.isBefore(windowStart, "day")) cursor.add(months[type], "months");
    while (cursor.isSameOrBefore(hardEnd, "day")) {
      add(cursor);
      cursor.add(months[type], "months");
    }
    return Array.from(dates).sort();
  }

  add(seriesStart);
  return Array.from(dates).sort();
};

/* ------------------------------------------------------------------ */
/* Row building                                                        */
/* ------------------------------------------------------------------ */

// Key for one missed visit. Matches what a rendered Home job produces via
// missedKeyForRenderedJob(), so Home can filter its own list by it.
const rowKey = (jobId, occurrenceKey) => `${jobId}__${occurrenceKey || "main"}`;

export const missedKeyForRenderedJob = (job) =>
  rowKey(job?.id, isRecurringJobRecord(job) ? getRecurringOccurrenceKey(job) : "main");

const techList = (job) =>
  (Array.isArray(job?.techAssign) ? job.techAssign : job?.techAssign ? [job.techAssign] : [])
    .filter(Boolean);

const makeRow = (job, { occurrenceKey, startKey, endKey, recurring, olderMissedKeys = [], today }) => {
  const renderJob = recurring
    ? applyOccurrenceOverrideToRenderedJob({
        ...job,
        startDate: occurrenceKey,
        endDate: occurrenceKey,
        occurrenceDate: occurrenceKey,
        originalOccurrenceDate: occurrenceKey,
      })
    : { ...job, startDate: startKey, occurrenceDate: startKey };

  const log = Array.isArray(job.missedLog) ? job.missedLog : [];

  return {
    key: rowKey(job.id, recurring ? occurrenceKey : "main"),
    jobId: job.id,
    job,
    renderJob,
    recurring,
    occurrenceKey: recurring ? occurrenceKey : "",
    missedDate: startKey,
    missedEndDate: endKey,
    spanDays: Math.max(daysBetween(startKey, endKey), 0),
    daysLate: Math.max(daysBetween(endKey, today), 1),
    olderMissedKeys,
    wo: job.wo || "",
    siteName: job.siteName || job.property || "",
    city: job.city || "",
    serviceType: job.serviceType || "",
    techs: techList(job),
    timesMissedBefore: log.length,
    lastLog: log.length ? log[log.length - 1] : null,
  };
};

/**
 * @param jobs        raw job docs ({ id, ...data })
 * @param pendingIds  Set of techCompletionRequests ids that are still pending
 * @returns rows (oldest first) with rows.pendingDoneCount = past-due jobs that
 *          are waiting in Ready to Invoice instead
 */
export const buildMissedJobRows = (jobs = [], pendingIds = new Set()) => {
  const today = todayKey();
  const yesterday = addDaysKey(today, -1);
  const rows = [];
  let pendingDoneCount = 0;

  (jobs || []).forEach((job) => {
    if (!job?.id) return;
    if (HIDDEN_SERVICE_TYPES.includes(job.serviceType)) return;

    const startKey = toKey(job.startDate);
    if (!startKey) return; // unscheduled -> Needs Scheduling, not missed

    // ---------- ONE-TIME ----------
    if (!isRecurringJobRecord(job)) {
      const status = String(job.status || "").trim().toLowerCase();
      if (CLOSED_STATUSES.includes(status)) return;

      const rawEnd = toKey(job.endDate);
      const endKey = rawEnd && rawEnd >= startKey ? rawEnd : startKey;
      if (endKey >= today) return; // still today / upcoming / in progress

      const row = makeRow(job, { startKey, endKey, recurring: false, today });
      if (pendingIds.has(getTechCompletionRequestId(row.renderJob))) {
        pendingDoneCount += 1;
        return;
      }
      rows.push(row);
      return;
    }

    // ---------- RECURRING ----------
    const excluded = getExcludedDateSet(job);
    const completed = getOccurrenceKeySet(job, "completedDates");
    const invoiced = getOccurrenceKeySet(job, "invoicedDates");
    const overrides = job.occurrenceOverrides || {};
    const recurrenceEnd = toKey(
      (typeof job.recurrence === "object" && (job.recurrence.endsOn || job.recurrence.endDate)) ||
        job.recurrenceEndDate
    );

    const isOpen = (key) =>
      !excluded.has(key) && !completed.has(key) && !invoiced.has(key);

    // candidates: { occurrenceKey, startKey, endKey }
    const candidates = [];

    // Visits the recurrence rule generated, up to yesterday. Visits that were
    // moved are handled below from their override instead.
    getRecurringOccurrenceKeys(job, startKey, yesterday).forEach((key) => {
      if (!isOpen(key)) return;
      if (overrides[key]) return;
      candidates.push({ occurrenceKey: key, startKey: key, endKey: key });
    });

    // Visits that were moved (Calendar / edit "this occurrence only").
    Object.entries(overrides).forEach(([rawKey, ov]) => {
      const key = toKey(rawKey);
      if (!key || !isOpen(key)) return;
      if (recurrenceEnd && key > recurrenceEnd) return;
      const movedStart = toKey(ov?.startDate || ov?.occurrenceDate) || key;
      const movedEndRaw = toKey(ov?.endDate);
      const movedEnd = movedEndRaw && movedEndRaw >= movedStart ? movedEndRaw : movedStart;
      if (movedEnd >= today) return;
      candidates.push({ occurrenceKey: key, startKey: movedStart, endKey: movedEnd });
    });

    if (!candidates.length) return;

    candidates.sort((a, b) => (a.endKey < b.endKey ? -1 : a.endKey > b.endKey ? 1 : 0));
    const latest = candidates[candidates.length - 1];
    const older = candidates.slice(0, -1).map((c) => c.occurrenceKey);

    const row = makeRow(job, {
      occurrenceKey: latest.occurrenceKey,
      startKey: latest.startKey,
      endKey: latest.endKey,
      recurring: true,
      olderMissedKeys: older,
      today,
    });
    if (pendingIds.has(getTechCompletionRequestId(row.renderJob))) {
      pendingDoneCount += 1;
      return;
    }
    rows.push(row);
  });

  // Oldest first - the longest-waiting customer is at the top.
  rows.sort((a, b) => (a.missedEndDate < b.missedEndDate ? -1 : a.missedEndDate > b.missedEndDate ? 1 : 0));
  // Past-due jobs a tech (or the office) already marked done - they are in
  // Ready to Invoice, not missed. Carried on the array so callers get both
  // numbers from one pass.
  rows.pendingDoneCount = pendingDoneCount;
  return rows;
};

/* ------------------------------------------------------------------ */
/* Pending tech completions (one small live query)                     */
/* ------------------------------------------------------------------ */

export const usePendingCompletionIds = (enabled = true) => {
  const [ids, setIds] = useState(() => new Set());

  useEffect(() => {
    if (!enabled) return undefined;
    const q = query(collection(db, TECH_COMPLETION_COLLECTION), where("status", "==", "pending"));
    const unsub = onSnapshot(
      q,
      (snap) => setIds(new Set(snap.docs.map((d) => d.id))),
      (err) => console.error("Missed jobs: pending completions listener failed:", err)
    );
    return () => unsub();
  }, [enabled]);

  return ids;
};

/* ------------------------------------------------------------------ */
/* "Remind me later" - once per day, per person, per browser           */
/* ------------------------------------------------------------------ */

const snoozeStorageKey = (uid) => `servicing.missedJobs.snooze.${uid || "anon"}`;

export const readSnooze = (uid) => {
  try {
    const raw = localStorage.getItem(snoozeStorageKey(uid));
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || parsed.date !== todayKey()) return { date: todayKey(), all: false, keys: [] };
    return { date: parsed.date, all: Boolean(parsed.all), keys: Array.isArray(parsed.keys) ? parsed.keys : [] };
  } catch (e) {
    return { date: todayKey(), all: false, keys: [] };
  }
};

const writeSnooze = (uid, value) => {
  try {
    localStorage.setItem(snoozeStorageKey(uid), JSON.stringify(value));
  } catch (e) {
    /* storage blocked - the popup simply shows again next open */
  }
  return value;
};

export const snoozeAllForToday = (uid) => writeSnooze(uid, { ...readSnooze(uid), all: true });

export const snoozeKeysForToday = (uid, keys = []) => {
  const current = readSnooze(uid);
  return writeSnooze(uid, { ...current, keys: Array.from(new Set([...current.keys, ...keys])) });
};

export const clearSnooze = (uid) => writeSnooze(uid, { date: todayKey(), all: false, keys: [] });

/* ------------------------------------------------------------------ */
/* Actions. Each returns { id, patch } to merge into local state.      */
/* ------------------------------------------------------------------ */

const buildLogEntry = (row, { action, reason = "", note = "", newDate = "", olderSkipped = 0, by = "", byUid = "" }) => ({
  occurrenceKey: row.occurrenceKey || "main",
  missedDate: row.missedDate,
  action,
  newDate,
  reason,
  note: String(note || "").trim(),
  olderSkipped,
  by,
  byUid,
  at: new Date().toISOString(),
});

const readLive = async (jobId) => {
  const snap = await getDoc(doc(db, "jobs", jobId));
  return snap.exists() ? snap.data() : null;
};

const mergeExcluded = (current, keys) =>
  Array.from(new Set([...(current?.excludedDates || []).map(toKey).filter(Boolean), ...keys]));

/** Move the missed visit to a new date. Keeps a multi-day job's length. */
export const rescheduleMissed = async (row, newStartKey, opts) => {
  const { skipOlder = false } = opts;
  const newEndKey = addDaysKey(newStartKey, row.spanDays);
  const jobRef = doc(db, "jobs", row.jobId);

  if (!row.recurring) {
    const entry = buildLogEntry(row, { ...opts, action: "rescheduled", newDate: newStartKey });
    await updateDoc(jobRef, {
      startDate: newStartKey,
      endDate: newEndKey,
      missedLog: arrayUnion(entry),
      updatedAt: serverTimestamp(),
    });
    return {
      id: row.jobId,
      patch: { startDate: newStartKey, endDate: newEndKey },
      logEntry: entry,
    };
  }

  // Recurring: move only this visit, keeping anything the visit already had
  // changed on it (tech, time window, ...).
  const live = (await readLive(row.jobId)) || row.job;
  const previousOverride = live?.occurrenceOverrides?.[row.occurrenceKey] || {};

  const result = await saveRecurringJobEdit({
    originalJob: {
      ...live,
      id: row.jobId,
      originalOccurrenceDate: row.occurrenceKey,
      occurrenceDate: row.occurrenceKey,
    },
    editedJob: { ...previousOverride, startDate: newStartKey, endDate: newEndKey },
    scope: "occurrence",
  });

  const older = skipOlder ? row.olderMissedKeys : [];
  const entry = buildLogEntry(row, {
    ...opts,
    action: "rescheduled",
    newDate: newStartKey,
    olderSkipped: older.length,
  });

  const extra = { missedLog: arrayUnion(entry), updatedAt: serverTimestamp() };
  let excludedDates;
  if (older.length) {
    const fresh = (await readLive(row.jobId)) || live;
    excludedDates = mergeExcluded(fresh, older);
    extra.excludedDates = excludedDates;
  }
  await updateDoc(jobRef, extra);

  const patch = { occurrenceOverrides: result?.masterJob?.occurrenceOverrides };
  if (excludedDates) patch.excludedDates = excludedDates;
  return { id: row.jobId, patch, logEntry: entry };
};

/** One-time jobs only: clear the date so the job is back on Needs Scheduling. */
export const sendMissedToNeedsScheduling = async (row, opts) => {
  if (row.recurring) throw new Error("Recurring jobs are rescheduled or skipped, not sent to Needs Scheduling.");
  const entry = buildLogEntry(row, { ...opts, action: "needsScheduling" });
  const update = {
    startDate: "",
    endDate: "",
    missedLog: arrayUnion(entry),
    updatedAt: serverTimestamp(),
  };
  // Needs Scheduling lists Active jobs - a job with no status would vanish.
  if (!String(row.job.status || "").trim()) update.status = "Active";

  await updateDoc(doc(db, "jobs", row.jobId), update);

  const patch = { startDate: "", endDate: "" };
  if (update.status) patch.status = update.status;
  return { id: row.jobId, patch, logEntry: entry };
};

/** Recurring only: skip this visit (and optionally the older missed ones). */
export const skipMissedVisit = async (row, opts) => {
  if (!row.recurring) throw new Error("Only recurring visits can be skipped.");
  const older = opts.skipOlder ? row.olderMissedKeys : [];
  const live = (await readLive(row.jobId)) || row.job;
  const excludedDates = mergeExcluded(live, [row.occurrenceKey, ...older]);
  const entry = buildLogEntry(row, { ...opts, action: "skipped", olderSkipped: older.length });

  await updateDoc(doc(db, "jobs", row.jobId), {
    excludedDates,
    missedLog: arrayUnion(entry),
    updatedAt: serverTimestamp(),
  });

  return { id: row.jobId, patch: { excludedDates }, logEntry: entry };
};

/**
 * It was done: send it to Ready to Invoice (the same queue a tech's
 * "Mark completed" lands in), so the office finishes it into an invoice the
 * normal way - nothing is billed or emailed from here.
 */
export const sendMissedToReadyToInvoice = async (row, opts) => {
  const older = opts.skipOlder && row.recurring ? row.olderMissedKeys : [];
  const note = [
    "Marked done from Missed Jobs by the office.",
    opts.reason ? `Reason: ${opts.reason}` : "",
    opts.note ? String(opts.note).trim() : "",
  ]
    .filter(Boolean)
    .join(" ");

  await submitTechCompletion(row.renderJob, {
    techName: `${opts.by || "Office"} (office)`,
    note,
    lang: "en",
  });

  const entry = buildLogEntry(row, { ...opts, action: "readyToInvoice", olderSkipped: older.length });
  const update = { missedLog: arrayUnion(entry), updatedAt: serverTimestamp() };
  let excludedDates;
  if (older.length) {
    const live = (await readLive(row.jobId)) || row.job;
    excludedDates = mergeExcluded(live, older);
    update.excludedDates = excludedDates;
  }
  await updateDoc(doc(db, "jobs", row.jobId), update);

  return {
    id: row.jobId,
    patch: excludedDates ? { excludedDates } : {},
    logEntry: entry,
    pendingRequestId: getTechCompletionRequestId(row.renderJob),
  };
};

/** Apply a list of { id, patch, logEntry } results to a jobs array. */
export const applyMissedPatches = (jobs, results = []) => {
  if (!results.length) return jobs;
  const byId = new Map();
  results.forEach((r) => {
    if (!r?.id) return;
    const prev = byId.get(r.id) || { patch: {}, logs: [] };
    byId.set(r.id, {
      patch: { ...prev.patch, ...(r.patch || {}) },
      logs: r.logEntry ? [...prev.logs, r.logEntry] : prev.logs,
    });
  });
  return jobs.map((job) => {
    const hit = byId.get(job.id);
    if (!hit) return job;
    const next = { ...job, ...hit.patch };
    Object.keys(hit.patch).forEach((k) => {
      if (hit.patch[k] === undefined) next[k] = job[k];
    });
    if (hit.logs.length) next.missedLog = [...(Array.isArray(job.missedLog) ? job.missedLog : []), ...hit.logs];
    return next;
  });
};
